import { ref } from 'vue';
import { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';
import axios from '../../../axios';
import {
    FetchPaginatedTicketsValidationError,
    Ticket,
    TicketTopic,
    ObjectResponse,
    PaginationMeta,
    CreateTicketValidationError,
    TicketAttachmentFile,
    TicketFetchingSourceType,
    FileType,
    CreateCommentValidationError,
    TicketContentType,
} from '@/types';
import { arrayToIndexObject, sortArrayBySorting } from '@/utils/objectUtils';
import { downloadBase64File } from '@/utils/fileUtils';
import { translateNameI18n } from '@/utils/i18nUtils';
import { useTicket, getStoredPageSize } from '../composables/ticket';

function extractAttachmentErrors(errors: Record<string, string[]>): string[] {
    return Object.entries(errors)
        .filter(([key]) => key.startsWith('attachments'))
        .flatMap(([, messages]) => messages);
}

function normalizeTicketValidationErrors(
    errors: Record<string, string[]>
): CreateTicketValidationError & Record<string, string[]> {
    const attachmentErrors = extractAttachmentErrors(errors);
    if (attachmentErrors.length === 0) {
        return errors as CreateTicketValidationError & Record<string, string[]>;
    }
    return {
        ...errors,
        attachments: attachmentErrors,
    };
}

function normalizeTicketCommentValidationErrors(
    errors: Record<string, string[]>
): CreateCommentValidationError & Record<string, string[]> {
    const attachmentErrors = extractAttachmentErrors(errors);
    if (attachmentErrors.length === 0) {
        return errors as CreateCommentValidationError & Record<string, string[]>;
    }
    return {
        ...errors,
        attachments: attachmentErrors,
    };
}

const endpointUrl = '/api/v1/tickets';

export const useTicketStore = defineStore('ticket', () => {
    const { sorting } = useTicket();

    const tickets = ref<{ [key: string]: Ticket }>({});
    const topics = ref<{ [key: string]: TicketTopic }>({});

    const isFetchingPaginatedTickets = ref(false);
    const pendingFetchPromise = ref<Promise<void> | null>(null);
    const isFetchingTicket = ref(false);
    const sourceType = ref<TicketFetchingSourceType | null>(null);
    const fetchPaginatedTicketsValidationError = ref<FetchPaginatedTicketsValidationError | null>(
        null
    );
    const fetchPaginatedTicketsMeta = ref<PaginationMeta | null>(null);

    const isCreatingTicket = ref(false);
    const createTicketValidationError = ref<CreateTicketValidationError | null>(null);

    const isCreatingComment = ref(false);
    const createCommentValidationError = ref<CreateCommentValidationError | null>(null);
    const isCreatingCommentAndFetchingTicket = ref(false);
    const isFetchingTopics = ref(false);

    const assignTicket = (ticket: Ticket) => {
        const ticketIndexObject = arrayToIndexObject([ticket]);
        Object.assign(tickets.value, ticketIndexObject);
    };

    const ensureFetchOnce = async (
        sourceTypeParam: TicketFetchingSourceType,
        sortings: string[],
        filters: { [key: string]: string },
        search: string
    ) => {
        if (getTickets().length > 0 && sourceType.value === sourceTypeParam) {
            return;
        }

        if (pendingFetchPromise.value) {
            return pendingFetchPromise.value;
        }

        pendingFetchPromise.value = (async () => {
            try {
                await fetchPaginatedTickets(
                    sourceTypeParam,
                    1,
                    getStoredPageSize(),
                    sortings,
                    filters,
                    search
                );
            } finally {
                pendingFetchPromise.value = null;
            }
        })();

        return pendingFetchPromise.value;
    };

    const fetchPaginatedTickets = async (
        sourceTypeParam: TicketFetchingSourceType,
        page = 1,
        itemsPerPage = 15,
        sortings = [] as string[],
        filters = {} as { [key: string]: string },
        search = ''
    ) => {
        try {
            isFetchingPaginatedTickets.value = true;
            const response: AxiosResponse<ObjectResponse> = await axios.get(endpointUrl, {
                params: {
                    page,
                    itemsPerPage,
                    sortings,
                    filters,
                    search,
                },
            });

            if (response.data.status === 422) {
                fetchPaginatedTicketsValidationError.value = response.data.errors;
            } else {
                fetchPaginatedTicketsValidationError.value = null;
                const ticketsData = response.data.data as Ticket[];
                tickets.value = arrayToIndexObject(ticketsData);
                fetchPaginatedTicketsMeta.value = response.data.meta;
                sourceType.value = sourceTypeParam;
            }
            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error);
        } finally {
            isFetchingPaginatedTickets.value = false;
        }
    };

    const fetchTicket = async (id: number) => {
        try {
            isFetchingTicket.value = true;
            const response: AxiosResponse<ObjectResponse> = await axios.get(`${endpointUrl}/${id}`);
            const ticket = response.data.data as Ticket;
            assignTicket(ticket);
            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error);
        } finally {
            isFetchingTicket.value = false;
        }
    };

    const getOrFetchTicket = async (id: number) => {
        const ticket = getTicket(id);
        if (!ticket) {
            await fetchTicket(id);
        }
    };

    const createTicket = async (
        fields: Record<string, unknown>,
        contentType = TicketContentType.Plain
    ) => {
        try {
            createTicketValidationError.value = null;
            isCreatingTicket.value = true;
            const response: AxiosResponse<ObjectResponse> = await axios.post(`${endpointUrl}`, {
                ...fields,
                content_type: contentType,
            });

            if (response.data.status === 422) {
                createTicketValidationError.value = normalizeTicketValidationErrors(
                    response.data.errors
                );
            } else {
                const newTicket = response.data.data as Ticket;
                assignTicket(newTicket);

                if (fetchPaginatedTicketsMeta.value && getTickets().length) {
                    if (
                        Number(getTickets().length) >
                        Number(fetchPaginatedTicketsMeta.value?.per_page)
                    ) {
                        const ticketsList = getTickets();
                        ticketsList.pop();
                        tickets.value = arrayToIndexObject(ticketsList);

                        fetchPaginatedTicketsMeta.value.total =
                            fetchPaginatedTicketsMeta.value.total + 1;
                    }
                }

                return Promise.resolve(newTicket);
            }
        } catch (error) {
            return Promise.reject(error);
        } finally {
            isCreatingTicket.value = false;
        }
    };

    const createHtmlTicket = async (fields: Record<string, unknown>) => {
        return createTicket(fields, TicketContentType.Html);
    };

    const createComment = async (
        ticketId: number,
        fields: {
            body: string;
            attachments?: Array<{ name: string; content_base64: string; content_type: string }>;
        },
        contentType = TicketContentType.Html
    ) => {
        try {
            createCommentValidationError.value = null;
            isCreatingComment.value = true;
            const response: AxiosResponse<ObjectResponse> = await axios.post(
                `${endpointUrl}/${ticketId}/comments`,
                { ...fields, content_type: contentType }
            );

            if (response.data.status === 422) {
                createCommentValidationError.value = normalizeTicketCommentValidationErrors(
                    response.data.errors
                );
            } else {
                await fetchTicket(ticketId);
            }
        } catch (error) {
            return Promise.reject(error);
        } finally {
            isCreatingComment.value = false;
        }
    };

    const createHtmlCommentAndFetchTicket = async (
        ticketId: number,
        fields: {
            body: string;
            attachments?: Array<{ name: string; content_base64: string; content_type: string }>;
        }
    ) => {
        try {
            isCreatingCommentAndFetchingTicket.value = true;
            await createComment(ticketId, fields, TicketContentType.Html);
            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error);
        } finally {
            isCreatingCommentAndFetchingTicket.value = false;
        }
    };

    const downloadAttachment = async (
        ticketId: number,
        commentId: number,
        attachmentId: number
    ) => {
        try {
            const response: AxiosResponse<ObjectResponse> = await axios.get(
                `${endpointUrl}/${ticketId}/comments/${commentId}/attachments/${attachmentId}`
            );
            const file = response.data.data as TicketAttachmentFile;

            downloadBase64File(file.content_type || FileType.Pdf, file.content_base64, file.name);
            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const fetchTopics = async () => {
        try {
            isFetchingTopics.value = true;
            const response: AxiosResponse<ObjectResponse> = await axios.get(
                `${endpointUrl}/topics`
            );
            const topicsData = response.data.data as TicketTopic[];
            topicsData.forEach((topic) => {
                translateNameI18n(topic);
            });
            topics.value = arrayToIndexObject(topicsData, 'key');
            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error);
        } finally {
            isFetchingTopics.value = false;
        }
    };

    const initTopicsSilently = async () => {
        if (!Object.keys(topics.value).length) {
            try {
                await fetchTopics();
            } catch {
                /* intentionally empty */
            }
        }
    };

    const getTickets = () => {
        const ticketsList = Object.values(tickets.value);
        return sortArrayBySorting(ticketsList, sorting.value);
    };

    const getTicket = (id: number) => {
        return tickets.value[id];
    };

    const getPublicTopics = () => {
        return Object.values(topics.value).filter((topic) => !topic.is_internal);
    };

    const getInternalTopics = () => {
        return Object.values(topics.value).filter((topic) => topic.is_internal);
    };

    return {
        tickets,
        topics,
        isFetchingPaginatedTickets,
        pendingFetchPromise,
        isFetchingTicket,
        isFetchingTopics,
        sourceType,
        fetchPaginatedTicketsValidationError,
        fetchPaginatedTicketsMeta,
        isCreatingTicket,
        createTicketValidationError,
        isCreatingComment,
        createCommentValidationError,
        isCreatingCommentAndFetchingTicket,
        assignTicket,
        ensureFetchOnce,
        fetchPaginatedTickets,
        fetchTicket,
        getOrFetchTicket,
        createTicket,
        createHtmlTicket,
        createComment,
        createHtmlCommentAndFetchTicket,
        downloadAttachment,
        fetchTopics,
        initTopicsSilently,
        getTickets,
        getTicket,
        getPublicTopics,
        getInternalTopics,
    };
});
